import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { SummaryCardData } from './types';

export interface SummaryCardProps extends SummaryCardData {
  onClick?: (id: string) => void;
  className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '8px',
      flex: '1 0 0',
      alignSelf: 'stretch',
      borderRadius: '12px',
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
      padding: '16px',
    },
    clickable: {
      cursor: 'pointer',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      width: '100%',
    },
    cardContent: {
      padding: '0 !important',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
    },
    title: {
      color: theme.palette.text.secondary,
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '-0.21px',
    },
    icon: {
      color: theme.palette.primary.main,
    },
    value: {
      color: theme.palette.text.primary,
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '-0.3px',
    },
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '-0.18px',
    },
  }),
);

export const SummaryCard: React.FC<SummaryCardProps> = ({
  id,
  title,
  value,
  subtitle,
  icon,
  onClick,
  className = '',
}) => {
  const classes = useStyles();

  const handleClick = () => {
    if (onClick) {
      onClick(id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Card
      elevation={0}
      className={`${classes.card} ${onClick ? classes.clickable : ''} ${className}`}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      <CardContent className={classes.cardContent}>
        <Box className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          {icon && <Box className={classes.icon}>{icon}</Box>}
        </Box>
        <Box>
          <Typography className={classes.value}>{value ?? 'N/A'}</Typography>
          {subtitle && (
            <Typography className={classes.subtitle}>{subtitle}</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
